import { isApiErrorBody, type ApiEndpoint } from "@/shared/api-endpoint";
import { truncateErrorProp } from "@/shared/telemetry-events.api";
import { kServerUrl } from "@/client/utils/serverUrl";
import { reportEvent } from "@/client/utils/event-reporter";
import {
    authTokenSignal,
    isAuthTokenStale,
    requestAuthToken,
    waitForAuthToken,
} from "@/client/core/services/auth-token.signal";

type EndpointBody<T> = T extends ApiEndpoint<infer TBody, unknown> ? TBody : never;
type EndpointSuccess<T> = T extends ApiEndpoint<unknown, infer TSuccess> ? TSuccess : never;

const kInitialTokenWaitMs = 3000;
const kTokenRefreshWaitMs = 5000;

const buildHeaders = (extraHeaders?: Record<string, string>): HeadersInit => {
    const headers: Record<string, string> = { ...extraHeaders };
    const token = authTokenSignal.value.accessToken;
    if (token !== undefined) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
};

export interface FetchFromServerInit {
    method?: string;
    headers?: Record<string, string>;
    body?: BodyInit;
}

/**
 * `fetch` against the Labs Live server with the auth semantics every server
 * request needs: wait briefly for the first token, attach it as a bearer
 * header, and retry once with a fresh token on 401. Use this (or
 * `postToServer`) for ALL requests to our server — a raw `fetch` sends no
 * token and is rejected once enforcement is on.
 */
export const fetchFromServer = async (
    url: string,
    init: FetchFromServerInit = {},
): Promise<Response> => {
    await waitForAuthToken(kInitialTokenWaitMs);
    // A token at the end of its life is refreshed before it is used, so an
    // expired one never reaches the server. The 401 path below stays as the
    // fallback for a token revoked ahead of its own expiry; it is skipped when
    // the refresh just tried and produced nothing, or a dead session would
    // wait out two refreshes before its 401 surfaced.
    let didRefreshFail = false;
    if (isAuthTokenStale()) {
        const staleToken = authTokenSignal.value.accessToken;
        await requestAuthToken(kTokenRefreshWaitMs);
        didRefreshFail = authTokenSignal.value.accessToken === staleToken;
    }

    const doFetch = async (): Promise<Response> =>
        await fetch(url, { ...init, headers: buildHeaders(init.headers) });

    let response = await doFetch();
    if (response.status === 401 && !didRefreshFail) {
        await requestAuthToken(kTokenRefreshWaitMs);
        response = await doFetch();
    }
    return response;
};

/**
 * Endpoints the platform itself owns — telemetry and output feedback. They are
 * plumbing, not something a user chose to run, and instrumenting the telemetry
 * endpoint would have it report on itself forever.
 *
 * `category` already carries this distinction for every endpoint in `shared/`
 * ("SpliceGen v3.0", "3P GenAI", "Vocal AI", … versus "Platform"), so the rule
 * reads off data the codebase already maintains instead of a list of routes
 * that would go stale.
 */
export const kPlatformEndpointCategory = "Platform";

const isCapability = (endpoint: ApiEndpoint<unknown, unknown>): boolean =>
    endpoint.category !== kPlatformEndpointCategory;

/**
 * Every capability call in every Labs app goes through here, so this is the one
 * place that has to know about it: an app gets requested / succeeded / failed
 * with a duration for free, named off the endpoint's own metadata, and app
 * authors never write a line of it.
 *
 * `capability_requested` is not redundant with the two outcomes. It is the
 * denominator: a generation the user abandoned, or one still running when the
 * panel closed, shows up as a request with no outcome, and that gap is exactly
 * what a slow model costs us.
 */
export interface PostToServerOptions {
    /**
     * A call the app made on its own behalf rather than one a user asked for —
     * a cache prewarm, a speculative fetch whose result is discarded.
     *
     * These are excluded from the capability events. Counting them would put
     * a requested/succeeded pair on the board for work no one asked for,
     * inflating usage for whichever model happens to be prewarmed and
     * depressing the audition and export rates measured against it. The call
     * still happens and still costs what it costs; it is simply not a person
     * choosing to run a tool, which is what these events are counting.
     */
    isBackground?: boolean;
}

export const postToServer = async <T extends ApiEndpoint<unknown, unknown>>(
    endpoint: T,
    body: EndpointBody<T>,
    options?: PostToServerOptions,
): Promise<EndpointSuccess<T> | Error> => {
    const url = `${kServerUrl}${endpoint.route}`;
    const shouldTrack = isCapability(endpoint) && options?.isBackground !== true;
    const capability = {
        capability: endpoint.label,
        source: endpoint.source,
        category: endpoint.category,
    };
    const startedAt = Date.now();

    if (shouldTrack) {
        reportEvent("capability_requested", capability);
    }

    const fail = (error: Error): Error => {
        if (shouldTrack) {
            reportEvent("capability_failed", {
                ...capability,
                duration_ms: Date.now() - startedAt,
                error: truncateErrorProp(error.message),
            });
        }
        return error;
    };

    // `fetchFromServer` rejects rather than returning on a network or CORS
    // failure, and callers have always handled that by catching. Reporting it
    // before re-throwing keeps that contract while closing the hole it would
    // otherwise leave: a dropped connection would look identical to a
    // generation the user walked away from.
    let response: Response;
    try {
        response = await fetchFromServer(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch (error) {
        fail(error instanceof Error ? error : new Error(String(error)));
        throw error;
    }

    if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
            // A JSON content-type is a promise the body does not have to keep.
            // An empty or truncated error body used to reject here, past the
            // point `fail` is reached, so a backend returning malformed JSON
            // was recorded as a request with no outcome — indistinguishable
            // from a user who walked away.
            const json: unknown = await response.json().catch(() => undefined);
            if (isApiErrorBody(json)) {
                return fail(new Error(json.error));
            }
        }
        return fail(
            new Error(
                `${endpoint.label} request failed. Error code: ${response.status}: ${response.statusText}`,
            ),
        );
    }

    // Same on the success path: a 2xx whose body will not parse is a failed
    // capability call, not a successful one and not an abandoned one.
    let json: unknown;
    try {
        json = await response.json();
    } catch (error) {
        return fail(
            new Error(
                `Invalid ${endpoint.label} response from server: ${
                    error instanceof Error ? error.message : String(error)
                }`,
            ),
        );
    }
    if (!endpoint.isSuccessResponse(json)) {
        return fail(new Error(`Invalid ${endpoint.label} response from server`));
    }

    if (shouldTrack) {
        reportEvent("capability_succeeded", {
            ...capability,
            duration_ms: Date.now() - startedAt,
        });
    }

    return json as EndpointSuccess<T>;
};
